import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { OwnerStats, Hostel, PageResponse } from '@/api/types';
import { CheckCircle, Building2, Clock, ChevronDown, ChevronRight, PauseCircle, RefreshCw, Send } from 'lucide-react';
import { SendNotificationModal } from '@/components/notifications/SendNotificationModal';
import type { SendTarget } from '@/components/notifications/SendNotificationModal';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination } from '@/hooks/usePagination';
import { hostelStatusLabel } from '@/lib/labels';

const HOSTEL_STATUS_COLORS: Record<string, string> = {
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  ACTIVE:           'bg-green-100 text-green-800',
  REJECTED:         'bg-red-100 text-red-700',
  SUSPENDED:        'bg-gray-100 text-gray-700',
};

function OwnerHostelsPanel({ ownerId }: { ownerId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<PageResponse<Hostel>>({
    queryKey: ['admin-owner-hostels', ownerId],
    queryFn: () => client.get(`/api/admin/hostels?ownerId=${ownerId}&size=100`).then(r => r as unknown as PageResponse<Hostel>),
  });

  const approve = useMutation({
    mutationFn: (id: string) => client.post(`/api/admin/hostels/${id}/approve`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-owner-hostels', ownerId] });
      void qc.invalidateQueries({ queryKey: ['admin-owners'] });
    },
  });

  const suspend = useMutation({
    mutationFn: (id: string) => client.post(`/api/admin/hostels/${id}/suspend`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-owner-hostels', ownerId] });
      void qc.invalidateQueries({ queryKey: ['admin-owners'] });
    },
  });

  const hostels = data?.content ?? [];

  if (isLoading) {
    return (
      <tr><td colSpan={7} className="px-6 py-4 bg-gray-50 text-gray-400 text-sm text-center">
        {t('admin.loadingHostels', 'Loading hostels…')}
      </td></tr>
    );
  }

  if (hostels.length === 0) {
    return (
      <tr><td colSpan={7} className="px-6 py-4 bg-gray-50">
        <p className="text-gray-400 text-sm text-center">{t('admin.noHostelsUnderOwner', 'No hostels registered under this owner')}</p>
      </td></tr>
    );
  }

  return (
    <tr>
      <td colSpan={7} className="p-0">
        <div className="bg-blue-50/50 border-y border-blue-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-blue-700 uppercase bg-blue-100/60">
                <th className="px-8 py-2 text-left w-8"></th>
                <th className="px-4 py-2 text-left">{t('hostel.hostelName')}</th>
                <th className="px-4 py-2 text-left">{t('admin.location', 'Location')}</th>
                <th className="px-4 py-2 text-left">{t('common.status')}</th>
                <th className="px-4 py-2 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-100/60">
              {hostels.map(h => (
                <tr key={h.id} className="hover:bg-blue-50 transition">
                  <td className="px-8 py-2.5">
                    <Building2 size={14} className="text-blue-400" />
                  </td>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-gray-800">{h.name}</p>
                    {h.phone && <p className="text-xs text-gray-400">{h.phone}</p>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">
                    {h.city}, {h.state}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${HOSTEL_STATUS_COLORS[h.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {hostelStatusLabel(t, h.status)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {h.status === 'PENDING_APPROVAL' && (
                        <button
                          onClick={() => approve.mutate(h.id)}
                          disabled={approve.isPending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium disabled:opacity-50"
                        >
                          <CheckCircle size={12} /> {t('admin.approve', 'Approve')}
                        </button>
                      )}
                      {h.status === 'ACTIVE' && (
                        <button
                          onClick={() => suspend.mutate(h.id)}
                          disabled={suspend.isPending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-medium disabled:opacity-50"
                        >
                          <PauseCircle size={12} /> {t('admin.suspend', 'Suspend')}
                        </button>
                      )}
                      {(h.status === 'SUSPENDED' || h.status === 'REJECTED') && (
                        <button
                          onClick={() => approve.mutate(h.id)}
                          disabled={approve.isPending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium disabled:opacity-50"
                        >
                          <RefreshCw size={12} /> {t('admin.activate', 'Activate')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}

export function AdminOwnersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'pending' | 'active'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [notifTarget, setNotifTarget] = useState<SendTarget | null>(null);
  const { page, size, setPage, resetPage } = usePagination();
  const isActiveParam = filter === 'pending' ? false : filter === 'active' ? true : undefined;

  function ownerStatusBadge(isActive: boolean) {
    return isActive
      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle size={11} />{t('common.active')}</span>
      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"><Clock size={11} />{t('rent.pending')}</span>;
  }

  const { data, isLoading } = useQuery<PageResponse<OwnerStats>>({
    queryKey: ['admin-owners', filter, page],
    queryFn: () => client.get(`/api/admin/owners?page=${page}&size=${size}${isActiveParam !== undefined ? `&isActive=${isActiveParam}` : ''}`).then(r => r as unknown as PageResponse<OwnerStats>),
  });

  const approve = useMutation({
    mutationFn: (id: string) => client.post(`/api/admin/owners/${id}/approve`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-owners'] }),
  });

  const suspend = useMutation({
    mutationFn: (id: string) => client.post(`/api/admin/owners/${id}/suspend`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-owners'] }),
  });

  const owners = data?.content ?? [];
  const pendingCount = filter === 'pending' ? (data?.totalElements ?? 0) : undefined;

  const tabs: { key: typeof filter; label: string }[] = [
    { key: 'all',     label: t('common.all') },
    { key: 'pending', label: `${t('rent.pending')}${pendingCount !== undefined ? ` (${pendingCount})` : ''}` },
    { key: 'active',  label: t('common.active') },
  ];

  const toggleExpand = (id: string) => setExpandedId(prev => prev === id ? null : id);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.ownerManagement', 'Owner Management')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('admin.ownerManagementSubtitle', 'Click an owner name to view and manage their hostels')}</p>
        </div>
        <div className="flex items-center gap-2">
          {filter === 'pending' && !!pendingCount && (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium">
              <Clock size={14} /> {t('admin.pendingApprovalCount', '{{count}} pending approval', { count: pendingCount })}
            </span>
          )}
          <button
            onClick={() => setNotifTarget({ type: 'broadcast' })}
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
          >
            <Send size={14} /> {t('admin.notifyAllOwners', 'Notify All Owners')}
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        {tabs.map(t2 => (
          <button
            key={t2.key}
            onClick={() => { setFilter(t2.key); resetPage(); }}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
              filter === t2.key ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t2.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : owners.length === 0 ? (
          <div className="p-8 text-center text-gray-400">{t('admin.noOwnersFound', 'No owners found')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="w-8 px-4 py-3"></th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('admin.owner', 'Owner')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('admin.company', 'Company')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('common.phone')}</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">{t('nav.hostels')}</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">{t('common.status')}</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {owners.map(owner => (
                <>
                  <tr
                    key={owner.id}
                    className={`border-b border-gray-50 transition cursor-pointer hover:bg-gray-50 ${expandedId === owner.id ? 'bg-blue-50/30' : ''}`}
                    onClick={() => toggleExpand(owner.id)}
                  >
                    <td className="px-4 py-3 text-gray-400">
                      {expandedId === owner.id
                        ? <ChevronDown size={15} className="text-blue-500" />
                        : <ChevronRight size={15} />}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-blue-700 hover:underline">{owner.fullName}</p>
                      <p className="text-xs text-gray-500">{owner.email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{owner.companyName || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{owner.phone ?? '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-gray-700">
                        <Building2 size={13} className="text-gray-400" />
                        {owner.hostelCount}
                        {owner.activeHostelCount > 0 && (
                          <span className="text-xs text-green-600 ml-1">({owner.activeHostelCount} {t('admin.active', 'active')})</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                      {ownerStatusBadge(owner.isActive)}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setNotifTarget({ type: 'user', userId: owner.id, name: owner.fullName })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 hover:bg-teal-50 text-teal-700 text-xs font-medium border border-gray-200 hover:border-teal-200 transition"
                        >
                          <Send size={12} /> {t('admin.notify', 'Notify')}
                        </button>
                        {!owner.isActive ? (
                          <button
                            onClick={() => approve.mutate(owner.id)}
                            disabled={approve.isPending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium disabled:opacity-50"
                          >
                            <CheckCircle size={13} /> {t('admin.approve', 'Approve')}
                          </button>
                        ) : (
                          <button
                            onClick={() => suspend.mutate(owner.id)}
                            disabled={suspend.isPending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-xs font-medium border border-red-200 disabled:opacity-50"
                          >
                            <PauseCircle size={13} /> {t('admin.suspend', 'Suspend')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === owner.id && <OwnerHostelsPanel ownerId={owner.id} />}
                </>
              ))}
            </tbody>
          </table>
        )}
        <Pagination
          page={page}
          totalPages={data?.totalPages ?? 0}
          totalElements={data?.totalElements ?? 0}
          size={size}
          onPageChange={setPage}
          className="px-4 pb-3"
        />
      </div>

      {notifTarget && (
        <SendNotificationModal
          target={notifTarget}
          recipientLabel={t('admin.allHostelOwners', 'All Hostel Owners')}
          onClose={() => setNotifTarget(null)}
        />
      )}
    </div>
  );
}
